import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { browserSupportsWebAuthn, startRegistration } from '@simplewebauthn/browser';

import { gqlClient } from '@/api';
import { fromTransport, type PasskeyFailure } from './auth';

/**
 * Registering, listing and removing the keys somebody signs in with.
 *
 * The API has answered all three since passkeys were built, and nothing called
 * them: the admin could *sign in* with a passkey but had no way to create one,
 * so the button on the login screen was a control that could only ever fail.
 * Its own error message said "Register one first" and pointed at a screen that
 * did not exist.
 */

export interface Passkey {
  id: string;
  name: string;
  createdAt: string;
  lastUsedAt: string | null;
}

const MY_PASSKEYS = gql`
  query MyPasskeys {
    myPasskeys {
      id
      name
      createdAt
      lastUsedAt
    }
  }
`;

const BEGIN_PASSKEY_REGISTRATION = gql`
  mutation BeginPasskeyRegistration {
    beginPasskeyRegistration {
      challengeId
      options
    }
  }
`;

const COMPLETE_PASSKEY_REGISTRATION = gql`
  mutation CompletePasskeyRegistration($input: CompletePasskeyRegistrationInput!) {
    completePasskeyRegistration(input: $input) {
      id
      name
      createdAt
      lastUsedAt
    }
  }
`;

const DELETE_PASSKEY = gql`
  mutation DeletePasskey($id: ID!) {
    deletePasskey(id: $id)
  }
`;

export const useMyPasskeys = () =>
  useQuery({
    queryKey: ['myPasskeys'],
    queryFn: async () => (await gqlClient.request<{ myPasskeys: Passkey[] }>(MY_PASSKEYS)).myPasskeys,
  });

/**
 * Adds a key on the device being used right now.
 *
 * Three steps that have to stay together, the same shape as signing in: ask the
 * server for a challenge, let the browser create the key, hand it back. The
 * middle step is the browser's own prompt, so somebody dismissing it is an
 * ordinary outcome rather than an error.
 *
 * Throws a `PasskeyFailure` rather than an `Error`, so the screen can say
 * which of the outcomes happened instead of showing one message for all of
 * them. `useMutation` carries it through `error` untouched.
 */
export const useRegisterPasskey = () => {
  const queryClient = useQueryClient();

  return useMutation<Passkey, PasskeyFailure, string>({
    mutationFn: async (name: string) => {
      if (!browserSupportsWebAuthn()) throw { kind: 'unsupported' } satisfies PasskeyFailure;

      let ceremony: { beginPasskeyRegistration: { challengeId: string; options: unknown } };
      try {
        ceremony = await gqlClient.request(BEGIN_PASSKEY_REGISTRATION);
      } catch (error) {
        throw fromTransport(error);
      }

      let credential: Record<string, unknown>;
      try {
        credential = (await startRegistration({
          optionsJSON: ceremony.beginPasskeyRegistration.options as never,
        })) as unknown as Record<string, unknown>;
      } catch {
        /*
         * Dismissing the prompt and a key this device already holds for this
         * account both arrive here, and the browser does not say which. The
         * screen's wording has to be honest about covering both.
         */
        throw { kind: 'no-passkey' } satisfies PasskeyFailure;
      }

      try {
        const saved = await gqlClient.request<{ completePasskeyRegistration: Passkey }>(
          COMPLETE_PASSKEY_REGISTRATION,
          { input: { challengeId: ceremony.beginPasskeyRegistration.challengeId, credential, name } },
        );
        return saved.completePasskeyRegistration;
      } catch (error) {
        throw fromTransport(error);
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['myPasskeys'] }),
  });
};

export const useDeletePasskey = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) =>
      (await gqlClient.request<{ deletePasskey: boolean }>(DELETE_PASSKEY, { id })).deletePasskey,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['myPasskeys'] }),
  });
};

/**
 * "3 days ago", for a list read at a glance.
 *
 * Never used gets its own words rather than a blank: a key registered and never
 * touched since is a real thing to notice — it may be on a device somebody no
 * longer has.
 */
export const lastUsed = (iso: string | null): string => {
  if (!iso) return 'never used';

  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days < 1) return 'used today';
  if (days === 1) return 'used yesterday';
  if (days < 30) return `used ${days} days ago`;
  return `used on ${new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium' }).format(new Date(iso))}`;
};

/* ---------------------------------------------------------------------- */

/**
 * Where this account is signed in.
 *
 * One entry per sign-in, not per stored row: the server groups the rotation
 * chain, because rotation replaces the token and keeps the rows it replaced so
 * that presenting one is detectable.
 */
export interface ActiveSession {
  id: string;
  device: string | null;
  ipAddress: string | null;
  startedAt: string;
  lastUsedAt: string;
  current: boolean;
}

const MY_SESSIONS = gql`
  query MySessions {
    mySessions {
      id
      device
      ipAddress
      startedAt
      lastUsedAt
      current
    }
  }
`;

const REVOKE_SESSION = gql`
  mutation RevokeSession($id: ID!) {
    revokeSession(id: $id)
  }
`;

const REVOKE_OTHER_SESSIONS = gql`
  mutation RevokeOtherSessions {
    revokeOtherSessions
  }
`;

export const useMySessions = () =>
  useQuery({
    queryKey: ['mySessions'],
    queryFn: async () =>
      (await gqlClient.request<{ mySessions: ActiveSession[] }>(MY_SESSIONS)).mySessions,
  });

export const useRevokeSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) =>
      (await gqlClient.request<{ revokeSession: boolean }>(REVOKE_SESSION, { id })).revokeSession,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['mySessions'] }),
  });
};

export const useRevokeOtherSessions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () =>
      (await gqlClient.request<{ revokeOtherSessions: number }>(REVOKE_OTHER_SESSIONS))
        .revokeOtherSessions,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['mySessions'] }),
  });
};

/**
 * A user-agent string as something somebody recognises.
 *
 * Deliberately coarse. A full user-agent is unreadable and mostly untrue —
 * every browser claims to be several others — and the question this list
 * answers is "do I recognise this?", which needs a browser and a platform, not
 * a version. Anything unrecognised is shown as it arrived rather than dropped:
 * a device nobody can name is exactly the entry worth looking at twice.
 */
export const deviceLabel = (agent: string | null): string => {
  if (!agent) return 'Unknown device';

  const browser = /Edg\//.test(agent)
    ? 'Edge'
    : /OPR\//.test(agent)
      ? 'Opera'
      : /Firefox\//.test(agent)
        ? 'Firefox'
        : /Chrome\//.test(agent)
          ? 'Chrome'
          : /Safari\//.test(agent)
            ? 'Safari'
            : null;

  const platform = /iPhone/.test(agent)
    ? 'iPhone'
    : /iPad/.test(agent)
      ? 'iPad'
      : /Macintosh|Mac OS X/.test(agent)
        ? 'macOS'
        : /Windows/.test(agent)
          ? 'Windows'
          : /Android/.test(agent)
            ? 'Android'
            : /Linux/.test(agent)
              ? 'Linux'
              : null;

  if (browser && platform) return `${browser} on ${platform}`;
  if (browser) return browser;
  if (platform) return platform;
  // Not a browser at all — a script, or something pretending badly. Worth
  // seeing verbatim rather than filed under "Unknown".
  return agent.slice(0, 60);
};
